/**
 * DSonic Studio Factory Presets
 * 20 predefined presets including TR-808 and TR-909 emulations
 * These presets cannot be overwritten by users
 */

// Default mixer levels for balanced volume across all engines
export const DEFAULT_MIXER_LEVELS = {
  analog_kick: 0.75,    // Kicks can be loud, reduce slightly
  membrane: 0.80,       // Snare membrane balanced
  hybrid_snare: 0.78,   // Snare slightly reduced
  clap: 0.82,           // Claps need presence
  closed_hat: 0.72,     // Hats can be piercing
  open_hat: 0.70,       // Open hats even more piercing
  cymbal: 0.68,         // Cymbals can be loud
  tom: 0.80,            // Toms balanced
  rim: 0.74,            // Rim shots can be sharp
  shaker: 0.88,         // Shakers often quiet
  impact: 0.80,         // Impact balanced
  fm: 0.78,             // FM can be bright
};

export const FACTORY_PRESETS = {
  // ============================================
  // Classic Drum Machines
  // ============================================

  'TR-808': {
    _description: 'Roland TR-808 emulation - Deep subs, crisp hats, resonant claps',
    _category: 'Classic',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS },
    _sequencer: {
      stepCount: 16,
      bpm: 120,
      swing: 0,
      lanes: {
        analog_kick: [
          { velocity: 3, accent: true }, { velocity: 0, accent: false },
          { velocity: 0, accent: false }, { velocity: 0, accent: false },
          { velocity: 2, accent: false }, { velocity: 0, accent: false },
          { velocity: 0, accent: false }, { velocity: 0, accent: false },
          { velocity: 3, accent: false }, { velocity: 0, accent: false },
          { velocity: 0, accent: false }, { velocity: 1, accent: false },
          { velocity: 2, accent: false }, { velocity: 0, accent: false },
          { velocity: 0, accent: false }, { velocity: 0, accent: false },
          ...Array(16).fill({ velocity: 0, accent: false }),
        ],
        hybrid_snare: [
          { velocity: 0, accent: false }, { velocity: 0, accent: false },
          { velocity: 0, accent: false }, { velocity: 0, accent: false },
          { velocity: 3, accent: true }, { velocity: 0, accent: false },
          { velocity: 0, accent: false }, { velocity: 0, accent: false },
          { velocity: 0, accent: false }, { velocity: 0, accent: false },
          { velocity: 0, accent: false }, { velocity: 0, accent: false },
          { velocity: 3, accent: false }, { velocity: 0, accent: false },
          { velocity: 0, accent: false }, { velocity: 0, accent: false },
          ...Array(16).fill({ velocity: 0, accent: false }),
        ],
        closed_hat: [
          { velocity: 2, accent: false }, { velocity: 0, accent: false },
          { velocity: 2, accent: false }, { velocity: 0, accent: false },
          { velocity: 2, accent: false }, { velocity: 0, accent: false },
          { velocity: 2, accent: false }, { velocity: 0, accent: false },
          { velocity: 2, accent: false }, { velocity: 0, accent: false },
          { velocity: 2, accent: false }, { velocity: 0, accent: false },
          { velocity: 2, accent: false }, { velocity: 0, accent: false },
          { velocity: 2, accent: false }, { velocity: 0, accent: false },
          ...Array(16).fill({ velocity: 0, accent: false }),
        ],
        open_hat: [
          { velocity: 0, accent: false }, { velocity: 0, accent: false },
          { velocity: 0, accent: false }, { velocity: 0, accent: false },
          { velocity: 0, accent: false }, { velocity: 0, accent: false },
          { velocity: 0, accent: false }, { velocity: 2, accent: false },
          { velocity: 0, accent: false }, { velocity: 0, accent: false },
          { velocity: 0, accent: false }, { velocity: 0, accent: false },
          { velocity: 0, accent: false }, { velocity: 0, accent: false },
          { velocity: 0, accent: false }, { velocity: 2, accent: false },
          ...Array(16).fill({ velocity: 0, accent: false }),
        ],
        membrane: Array(32).fill({ velocity: 0, accent: false }),
        clap: Array(32).fill({ velocity: 0, accent: false }),
        cymbal: Array(32).fill({ velocity: 0, accent: false }),
        tom: Array(32).fill({ velocity: 0, accent: false }),
        rim: Array(32).fill({ velocity: 0, accent: false }),
        shaker: Array(32).fill({ velocity: 0, accent: false }),
        impact: Array(32).fill({ velocity: 0, accent: false }),
        fm: Array(32).fill({ velocity: 0, accent: false }),
      },
    },
    analog_kick: {
      bodyPitch: 42,
      bodyDecay: 0.55,
      pitchEnvAmt: 48,
      pitchEnvDecay: 0.12,
      clickLevel: 0.15,
      clickTone: 0.3,
      driveAmt: 0.1,
      lowShelfGain: 4,
      decay: 0.6,
    },
    membrane: {
      pitch1: 180,
      pitch2: 240,
      coupling12: 0.2,
      wireLevel: 0.4,
      snareWireTension: 0.5,
      tension: 0.4,
      strikeType: 0.3,
      strikeHardness: 0.5,
      decay: 0.18,
    },
    hybrid_snare: {
      bodyPitch: 200,
      wireLevel: 0.35,
      wireTension: 0.5,
      noiseLevel: 0.55,
      noiseNarrowAmt: 0.4,
      noiseWideAmt: 0.2,
      filterEnvAmt: 0.25,
      bumpAmt: 0.35,
      driveAmt: 0.1,
      bodyDecay: 0.14,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.3,
      filterEnvAmt: 0.35,
      transientLevel: 0.5,
      earlyReflections: 0.35,
      decay: 0.22,
    },
    closed_hat: {
      oscTune: 1.0,
      hiFilterFreq: 7100,
      loFilterFreq: 3440,
      noiseLevel: 0.25,
      transientLevel: 0.35,
      spectralDecay: 0.4,
      sizzleDepth: 0.2,
      driveAmt: 0.12,
      decay: 0.045,
    },
    open_hat: {
      oscTune: 1.0,
      hiFilterFreq: 7100,
      loFilterFreq: 3440,
      noiseLevel: 0.3,
      transientLevel: 0.35,
      spectralDecay: 0.45,
      hpfCutoff: 4000,
      sizzleDepth: 0.4,
      driveAmt: 0.12,
      decay: 0.30,
    },
    cymbal: {
      pitch: 320,
      size: 0.5,
      richness: 0.55,
      clusterDensity: 0.45,
      metalCharacter: 0.4,
      topAmt: 0.35,
      decay: 1.8,
    },
    tom: {
      pitch: 100,
      pitchEnvAmt: 10,
      pitchRampDecay: 0.08,
      tension: 0.5,
      overtoneAmt: 0.3,
      clickLevel: 0.2,
      decay: 0.35,
    },
    rim: {
      pitch: 1100,
      dualStrike: 0,
      enhance: 0.55,
      feedbackAmt: 0.25,
      snareTimbreAmt: 0,
      driveAmt: 0.15,
      decay: 0.06,
    },
    shaker: {
      rattleType: 0.3,
      grainSize: 0.015,
      grainDensity: 90,
      hardness: 0.45,
      tone: 0.5,
      noiseColor: 0.5,
      decay: 0.25,
    },
    fold: {
      pitch: 50,
      foldAmt: 0.4,
      foldSymmetry: 0.5,
      tablePosition: 0,
      tableWarp: 0,
      pitchEnvAmt: 12,
      filterRes: 0.2,
      decay: 0.4,
    },
    impact: {
      transientType: 'mallet',
      transientBrightness: 0.5,
      transientLevel: 0.6,
      bodyType: 'sine',
      bodyPitch: 200,
      bodyDecay: 0.4,
      tailType: 'reverb-like',
      tailDecay: 0.8,
      tailColor: 0.5,
      layerMix: 0.6,
      overallDecay: 0.6,
    },
    fm: {
      pitch: 55,
      modIndex: 4,
      modIndexDecay: 0.12,
      brightness: 0.4,
      inharmonicityBias: 0,
      driveAmt: 0.15,
      decay: 0.35,
    },
  },

  'TR-909': {
    _description: 'Roland TR-909 emulation - Punchy kicks, aggressive snares, metallic hats',
    _category: 'Classic',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS },
    analog_kick: {
      bodyPitch: 52,
      bodyDecay: 0.28,
      pitchEnvAmt: 36,
      pitchEnvDecay: 0.05,
      clickLevel: 0.45,
      clickTone: 0.55,
      driveAmt: 0.25,
      lowShelfGain: 2,
      decay: 0.32,
    },
    membrane: {
      pitch1: 200,
      pitch2: 260,
      coupling12: 0.35,
      wireLevel: 0.65,
      snareWireTension: 0.7,
      tension: 0.55,
      strikeType: 0.5,
      strikeHardness: 0.7,
      decay: 0.12,
    },
    hybrid_snare: {
      bodyPitch: 220,
      wireLevel: 0.5,
      wireTension: 0.65,
      noiseLevel: 0.7,
      noiseNarrowAmt: 0.55,
      noiseWideAmt: 0.35,
      filterEnvAmt: 0.4,
      bumpAmt: 0.5,
      driveAmt: 0.25,
      bodyDecay: 0.11,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.35,
      filterEnvAmt: 0.45,
      transientLevel: 0.65,
      earlyReflections: 0.25,
      decay: 0.18,
    },
    closed_hat: {
      oscTune: 1.1,
      hiFilterFreq: 7500,
      loFilterFreq: 3600,
      noiseLevel: 0.35,
      transientLevel: 0.45,
      spectralDecay: 0.5,
      sizzleDepth: 0.35,
      driveAmt: 0.18,
      decay: 0.035,
    },
    open_hat: {
      oscTune: 1.1,
      hiFilterFreq: 7500,
      loFilterFreq: 3600,
      noiseLevel: 0.4,
      transientLevel: 0.45,
      spectralDecay: 0.55,
      hpfCutoff: 4000,
      sizzleDepth: 0.55,
      driveAmt: 0.18,
      decay: 0.30,
    },
    cymbal: {
      pitch: 380,
      size: 0.45,
      richness: 0.65,
      clusterDensity: 0.55,
      metalCharacter: 0.5,
      topAmt: 0.45,
      decay: 2.2,
    },
    tom: {
      pitch: 120,
      pitchEnvAmt: 8,
      pitchRampDecay: 0.06,
      tension: 0.6,
      overtoneAmt: 0.35,
      clickLevel: 0.35,
      decay: 0.28,
    },
    rim: {
      pitch: 1200,
      dualStrike: 0.1,
      enhance: 0.6,
      feedbackAmt: 0.3,
      snareTimbreAmt: 0.1,
      driveAmt: 0.2,
      decay: 0.055,
    },
    shaker: {
      rattleType: 0.5,
      grainSize: 0.012,
      grainDensity: 100,
      hardness: 0.55,
      tone: 0.55,
      noiseColor: 0.55,
      decay: 0.22,
    },
    fold: {
      pitch: 55,
      foldAmt: 0.5,
      foldSymmetry: 0.45,
      tablePosition: 0.1,
      tableWarp: 0.05,
      pitchEnvAmt: 18,
      filterRes: 0.25,
      decay: 0.35,
    },
    impact: {
      transientType: 'click',
      transientBrightness: 0.6,
      transientLevel: 0.7,
      bodyType: 'triangle',
      bodyPitch: 250,
      bodyDecay: 0.3,
      tailType: 'damped',
      tailDecay: 0.6,
      tailColor: 0.55,
      layerMix: 0.55,
      overallDecay: 0.5,
    },
    fm: {
      pitch: 60,
      modIndex: 5,
      modIndexDecay: 0.08,
      brightness: 0.5,
      inharmonicityBias: 0.05,
      driveAmt: 0.2,
      decay: 0.3,
    },
  },

  // ============================================
  // Electronic Genres
  // ============================================

  'Deep House': {
    _description: 'Warm, deep groove - soft kick, shuffled hats, organic percussion',
    _category: 'Electronic',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS },
    analog_kick: {
      bodyPitch: 48,
      bodyDecay: 0.4,
      pitchEnvAmt: 30,
      pitchEnvDecay: 0.08,
      clickLevel: 0.2,
      clickTone: 0.4,
      driveAmt: 0.12,
      lowShelfGain: 3,
      decay: 0.45,
    },
    membrane: {
      pitch1: 170,
      pitch2: 210,
      coupling12: 0.3,
      wireLevel: 0.35,
      snareWireTension: 0.45,
      tension: 0.45,
      strikeType: 0.4,
      strikeHardness: 0.4,
      decay: 0.2,
    },
    hybrid_snare: {
      bodyPitch: 170,
      wireLevel: 0.3,
      wireTension: 0.4,
      noiseLevel: 0.45,
      noiseNarrowAmt: 0.35,
      noiseWideAmt: 0.2,
      filterEnvAmt: 0.2,
      bumpAmt: 0.3,
      driveAmt: 0.1,
      bodyDecay: 0.16,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.35,
      filterEnvAmt: 0.3,
      transientLevel: 0.4,
      earlyReflections: 0.4,
      decay: 0.25,
    },
    closed_hat: {
      oscTune: 0.95,
      hiFilterFreq: 6800,
      loFilterFreq: 3200,
      noiseLevel: 0.25,
      transientLevel: 0.3,
      spectralDecay: 0.4,
      sizzleDepth: 0.25,
      driveAmt: 0.1,
      decay: 0.055,
    },
    open_hat: {
      oscTune: 0.95,
      hiFilterFreq: 6800,
      loFilterFreq: 3200,
      noiseLevel: 0.3,
      transientLevel: 0.3,
      spectralDecay: 0.45,
      hpfCutoff: 4000,
      sizzleDepth: 0.45,
      driveAmt: 0.1,
      decay: 0.30,
    },
    cymbal: {
      pitch: 300,
      size: 0.55,
      richness: 0.5,
      clusterDensity: 0.4,
      metalCharacter: 0.35,
      topAmt: 0.3,
      decay: 1.5,
    },
    tom: {
      pitch: 90,
      pitchEnvAmt: 6,
      pitchRampDecay: 0.1,
      tension: 0.45,
      overtoneAmt: 0.25,
      clickLevel: 0.15,
      decay: 0.4,
    },
    rim: {
      pitch: 900,
      dualStrike: 0.05,
      enhance: 0.45,
      feedbackAmt: 0.2,
      snareTimbreAmt: 0,
      driveAmt: 0.1,
      decay: 0.07,
    },
    shaker: {
      rattleType: 0.4,
      grainSize: 0.018,
      grainDensity: 70,
      hardness: 0.4,
      tone: 0.45,
      noiseColor: 0.45,
      decay: 0.3,
    },
    fold: {
      pitch: 45,
      foldAmt: 0.3,
      foldSymmetry: 0.5,
      tablePosition: 0.1,
      tableWarp: 0.05,
      pitchEnvAmt: 8,
      filterRes: 0.2,
      decay: 0.45,
    },
    impact: {
      transientType: 'brush',
      transientBrightness: 0.4,
      transientLevel: 0.5,
      bodyType: 'filtered-noise',
      bodyPitch: 180,
      bodyDecay: 0.5,
      tailType: 'noise-tail',
      tailDecay: 0.9,
      tailColor: 0.4,
      layerMix: 0.5,
      overallDecay: 0.7,
    },
    fm: {
      pitch: 50,
      modIndex: 3,
      modIndexDecay: 0.15,
      brightness: 0.35,
      inharmonicityBias: -0.1,
      driveAmt: 0.1,
      decay: 0.4,
    },
  },

  'Hard Techno': {
    _description: 'Industrial power - distorted kicks, harsh snares, aggressive hats',
    _category: 'Electronic',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS, analog_kick: 0.70, closed_hat: 0.68, open_hat: 0.65 },
    analog_kick: {
      bodyPitch: 50,
      bodyDecay: 0.2,
      pitchEnvAmt: 42,
      pitchEnvDecay: 0.035,
      clickLevel: 0.6,
      clickTone: 0.7,
      driveAmt: 0.5,
      lowShelfGain: 1,
      decay: 0.25,
    },
    membrane: {
      pitch1: 220,
      pitch2: 300,
      coupling12: 0.45,
      wireLevel: 0.7,
      snareWireTension: 0.8,
      tension: 0.7,
      strikeType: 0.7,
      strikeHardness: 0.85,
      decay: 0.09,
    },
    hybrid_snare: {
      bodyPitch: 240,
      wireLevel: 0.6,
      wireTension: 0.75,
      noiseLevel: 0.8,
      noiseNarrowAmt: 0.65,
      noiseWideAmt: 0.45,
      filterEnvAmt: 0.5,
      bumpAmt: 0.6,
      driveAmt: 0.45,
      bodyDecay: 0.08,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.4,
      filterEnvAmt: 0.55,
      transientLevel: 0.8,
      earlyReflections: 0.15,
      decay: 0.14,
    },
    closed_hat: {
      oscTune: 1.2,
      hiFilterFreq: 8500,
      loFilterFreq: 4000,
      noiseLevel: 0.45,
      transientLevel: 0.55,
      spectralDecay: 0.6,
      sizzleDepth: 0.5,
      driveAmt: 0.35,
      decay: 0.028,
    },
    open_hat: {
      oscTune: 1.2,
      hiFilterFreq: 8500,
      loFilterFreq: 4000,
      noiseLevel: 0.5,
      transientLevel: 0.55,
      spectralDecay: 0.65,
      hpfCutoff: 4500,
      sizzleDepth: 0.7,
      driveAmt: 0.35,
      decay: 0.25,
    },
    cymbal: {
      pitch: 450,
      size: 0.35,
      richness: 0.75,
      clusterDensity: 0.7,
      metalCharacter: 0.65,
      topAmt: 0.55,
      decay: 1.2,
    },
    tom: {
      pitch: 140,
      pitchEnvAmt: 16,
      pitchRampDecay: 0.04,
      tension: 0.75,
      overtoneAmt: 0.5,
      clickLevel: 0.55,
      decay: 0.2,
    },
    rim: {
      pitch: 1400,
      dualStrike: 0.2,
      enhance: 0.7,
      feedbackAmt: 0.4,
      snareTimbreAmt: 0.15,
      driveAmt: 0.35,
      decay: 0.045,
    },
    shaker: {
      rattleType: 0.7,
      grainSize: 0.008,
      grainDensity: 150,
      hardness: 0.75,
      tone: 0.7,
      noiseColor: 0.65,
      decay: 0.15,
    },
    fold: {
      pitch: 60,
      foldAmt: 0.75,
      foldSymmetry: 0.35,
      tablePosition: 0.3,
      tableWarp: 0.2,
      pitchEnvAmt: 24,
      filterRes: 0.4,
      decay: 0.25,
    },
    impact: {
      transientType: 'impulse',
      transientBrightness: 0.8,
      transientLevel: 0.8,
      bodyType: 'resonant',
      bodyPitch: 300,
      bodyDecay: 0.2,
      tailType: 'damped',
      tailDecay: 0.4,
      tailColor: 0.7,
      layerMix: 0.45,
      overallDecay: 0.35,
    },
    fm: {
      pitch: 65,
      modIndex: 8,
      modIndexDecay: 0.04,
      brightness: 0.7,
      inharmonicityBias: 0.15,
      driveAmt: 0.4,
      decay: 0.22,
    },
  },

  'Minimal Tech': {
    _description: 'Less is more - subtle textures, hypnotic grooves, restrained dynamics',
    _category: 'Electronic',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS },
    analog_kick: {
      bodyPitch: 46,
      bodyDecay: 0.35,
      pitchEnvAmt: 28,
      pitchEnvDecay: 0.07,
      clickLevel: 0.25,
      clickTone: 0.45,
      driveAmt: 0.08,
      lowShelfGain: 2,
      decay: 0.38,
    },
    membrane: {
      pitch1: 185,
      pitch2: 225,
      coupling12: 0.25,
      wireLevel: 0.3,
      snareWireTension: 0.5,
      tension: 0.5,
      strikeType: 0.4,
      strikeHardness: 0.5,
      decay: 0.15,
    },
    hybrid_snare: {
      bodyPitch: 190,
      wireLevel: 0.25,
      wireTension: 0.45,
      noiseLevel: 0.4,
      noiseNarrowAmt: 0.35,
      noiseWideAmt: 0.15,
      filterEnvAmt: 0.2,
      bumpAmt: 0.25,
      driveAmt: 0.08,
      bodyDecay: 0.13,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.25,
      filterEnvAmt: 0.3,
      transientLevel: 0.35,
      earlyReflections: 0.3,
      decay: 0.2,
    },
    closed_hat: {
      oscTune: 0.95,
      hiFilterFreq: 6500,
      loFilterFreq: 3200,
      noiseLevel: 0.2,
      transientLevel: 0.3,
      spectralDecay: 0.4,
      sizzleDepth: 0.15,
      driveAmt: 0.08,
      decay: 0.05,
    },
    open_hat: {
      oscTune: 0.95,
      hiFilterFreq: 6500,
      loFilterFreq: 3200,
      noiseLevel: 0.25,
      transientLevel: 0.3,
      spectralDecay: 0.4,
      hpfCutoff: 4000,
      sizzleDepth: 0.35,
      driveAmt: 0.08,
      decay: 0.30,
    },
    cymbal: {
      pitch: 340,
      size: 0.45,
      richness: 0.45,
      clusterDensity: 0.4,
      metalCharacter: 0.4,
      topAmt: 0.35,
      decay: 1.3,
    },
    tom: {
      pitch: 110,
      pitchEnvAmt: 5,
      pitchRampDecay: 0.08,
      tension: 0.5,
      overtoneAmt: 0.2,
      clickLevel: 0.2,
      decay: 0.32,
    },
    rim: {
      pitch: 1000,
      dualStrike: 0,
      enhance: 0.5,
      feedbackAmt: 0.2,
      snareTimbreAmt: 0,
      driveAmt: 0.12,
      decay: 0.065,
    },
    shaker: {
      rattleType: 0.35,
      grainSize: 0.015,
      grainDensity: 75,
      hardness: 0.45,
      tone: 0.5,
      noiseColor: 0.5,
      decay: 0.25,
    },
    fold: {
      pitch: 48,
      foldAmt: 0.35,
      foldSymmetry: 0.5,
      tablePosition: 0.05,
      tableWarp: 0.02,
      pitchEnvAmt: 10,
      filterRes: 0.2,
      decay: 0.38,
    },
    impact: {
      transientType: 'mallet',
      transientBrightness: 0.45,
      transientLevel: 0.55,
      bodyType: 'sine',
      bodyPitch: 200,
      bodyDecay: 0.4,
      tailType: 'reverb-like',
      tailDecay: 0.7,
      tailColor: 0.5,
      layerMix: 0.55,
      overallDecay: 0.55,
    },
    fm: {
      pitch: 52,
      modIndex: 3.5,
      modIndexDecay: 0.1,
      brightness: 0.4,
      inharmonicityBias: 0,
      driveAmt: 0.12,
      decay: 0.35,
    },
  },

  'Ambient Dub': {
    _description: 'Spacious and ethereal - long tails, soft transients, washy textures',
    _category: 'Electronic',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS, cymbal: 0.65, open_hat: 0.68 },
    analog_kick: {
      bodyPitch: 44,
      bodyDecay: 0.5,
      pitchEnvAmt: 24,
      pitchEnvDecay: 0.1,
      clickLevel: 0.1,
      clickTone: 0.3,
      driveAmt: 0.05,
      lowShelfGain: 4,
      decay: 0.55,
    },
    membrane: {
      pitch1: 160,
      pitch2: 200,
      coupling12: 0.35,
      wireLevel: 0.25,
      snareWireTension: 0.4,
      tension: 0.4,
      strikeType: 0.3,
      strikeHardness: 0.35,
      decay: 0.25,
    },
    hybrid_snare: {
      bodyPitch: 160,
      wireLevel: 0.2,
      wireTension: 0.35,
      noiseLevel: 0.35,
      noiseNarrowAmt: 0.25,
      noiseWideAmt: 0.15,
      filterEnvAmt: 0.15,
      bumpAmt: 0.2,
      driveAmt: 0.05,
      bodyDecay: 0.22,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.35,
      filterEnvAmt: 0.25,
      transientLevel: 0.25,
      earlyReflections: 0.6,
      decay: 0.35,
    },
    closed_hat: {
      oscTune: 0.9,
      hiFilterFreq: 6200,
      loFilterFreq: 3000,
      noiseLevel: 0.3,
      transientLevel: 0.25,
      spectralDecay: 0.5,
      sizzleDepth: 0.4,
      driveAmt: 0.06,
      decay: 0.12,
    },
    open_hat: {
      oscTune: 0.9,
      hiFilterFreq: 6200,
      loFilterFreq: 3000,
      noiseLevel: 0.35,
      transientLevel: 0.25,
      spectralDecay: 0.55,
      hpfCutoff: 3500,
      sizzleDepth: 0.6,
      driveAmt: 0.06,
      decay: 0.60,
    },
    cymbal: {
      pitch: 280,
      size: 0.7,
      richness: 0.6,
      clusterDensity: 0.35,
      metalCharacter: 0.3,
      topAmt: 0.25,
      decay: 2.8,
    },
    tom: {
      pitch: 85,
      pitchEnvAmt: 4,
      pitchRampDecay: 0.12,
      tension: 0.4,
      overtoneAmt: 0.35,
      clickLevel: 0.1,
      decay: 0.55,
    },
    rim: {
      pitch: 850,
      dualStrike: 0.1,
      enhance: 0.4,
      feedbackAmt: 0.3,
      snareTimbreAmt: 0,
      driveAmt: 0.08,
      decay: 0.1,
    },
    shaker: {
      rattleType: 0.25,
      grainSize: 0.025,
      grainDensity: 55,
      hardness: 0.35,
      tone: 0.4,
      noiseColor: 0.4,
      decay: 0.4,
    },
    fold: {
      pitch: 42,
      foldAmt: 0.2,
      foldSymmetry: 0.55,
      tablePosition: 0.15,
      tableWarp: 0.1,
      pitchEnvAmt: 6,
      filterRes: 0.25,
      decay: 0.5,
    },
    impact: {
      transientType: 'brush',
      transientBrightness: 0.35,
      transientLevel: 0.4,
      bodyType: 'sine',
      bodyPitch: 160,
      bodyDecay: 0.8,
      tailType: 'shimmer',
      tailDecay: 1.5,
      tailColor: 0.4,
      layerMix: 0.6,
      overallDecay: 1.2,
    },
    fm: {
      pitch: 48,
      modIndex: 2.5,
      modIndexDecay: 0.2,
      brightness: 0.3,
      inharmonicityBias: -0.15,
      driveAmt: 0.05,
      decay: 0.5,
    },
  },

  'Electro Funk': {
    _description: 'Funky machine groove - snappy snares, bouncy kick, syncopated percussion',
    _category: 'Electronic',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS },
    analog_kick: {
      bodyPitch: 55,
      bodyDecay: 0.22,
      pitchEnvAmt: 32,
      pitchEnvDecay: 0.05,
      clickLevel: 0.35,
      clickTone: 0.5,
      driveAmt: 0.18,
      lowShelfGain: 2,
      decay: 0.28,
    },
    membrane: {
      pitch1: 195,
      pitch2: 245,
      coupling12: 0.35,
      wireLevel: 0.55,
      snareWireTension: 0.65,
      tension: 0.6,
      strikeType: 0.55,
      strikeHardness: 0.65,
      decay: 0.11,
    },
    hybrid_snare: {
      bodyPitch: 205,
      wireLevel: 0.4,
      wireTension: 0.55,
      noiseLevel: 0.55,
      noiseNarrowAmt: 0.45,
      noiseWideAmt: 0.25,
      filterEnvAmt: 0.35,
      bumpAmt: 0.4,
      driveAmt: 0.18,
      bodyDecay: 0.1,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.35,
      filterEnvAmt: 0.4,
      transientLevel: 0.6,
      earlyReflections: 0.2,
      decay: 0.16,
    },
    closed_hat: {
      oscTune: 1.05,
      hiFilterFreq: 7200,
      loFilterFreq: 3500,
      noiseLevel: 0.3,
      transientLevel: 0.4,
      spectralDecay: 0.45,
      sizzleDepth: 0.25,
      driveAmt: 0.15,
      decay: 0.04,
    },
    open_hat: {
      oscTune: 1.05,
      hiFilterFreq: 7200,
      loFilterFreq: 3500,
      noiseLevel: 0.35,
      transientLevel: 0.4,
      spectralDecay: 0.5,
      hpfCutoff: 4000,
      sizzleDepth: 0.45,
      driveAmt: 0.15,
      decay: 0.30,
    },
    cymbal: {
      pitch: 360,
      size: 0.4,
      richness: 0.55,
      clusterDensity: 0.5,
      metalCharacter: 0.45,
      topAmt: 0.4,
      decay: 1.4,
    },
    tom: {
      pitch: 130,
      pitchEnvAmt: 10,
      pitchRampDecay: 0.05,
      tension: 0.6,
      overtoneAmt: 0.35,
      clickLevel: 0.35,
      decay: 0.25,
    },
    rim: {
      pitch: 1150,
      dualStrike: 0.15,
      enhance: 0.6,
      feedbackAmt: 0.3,
      snareTimbreAmt: 0.05,
      driveAmt: 0.18,
      decay: 0.05,
    },
    shaker: {
      rattleType: 0.55,
      grainSize: 0.012,
      grainDensity: 95,
      hardness: 0.55,
      tone: 0.6,
      noiseColor: 0.55,
      decay: 0.2,
    },
    fold: {
      pitch: 58,
      foldAmt: 0.45,
      foldSymmetry: 0.45,
      tablePosition: 0.1,
      tableWarp: 0.08,
      pitchEnvAmt: 14,
      filterRes: 0.3,
      decay: 0.28,
    },
    impact: {
      transientType: 'click',
      transientBrightness: 0.55,
      transientLevel: 0.65,
      bodyType: 'triangle',
      bodyPitch: 260,
      bodyDecay: 0.3,
      tailType: 'damped',
      tailDecay: 0.55,
      tailColor: 0.55,
      layerMix: 0.5,
      overallDecay: 0.45,
    },
    fm: {
      pitch: 58,
      modIndex: 4.5,
      modIndexDecay: 0.07,
      brightness: 0.5,
      inharmonicityBias: 0.05,
      driveAmt: 0.18,
      decay: 0.28,
    },
  },

  'Drum & Bass': {
    _description: 'Jungle pressure - fast breaks, rolling subs, crispy tops',
    _category: 'Electronic',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS, analog_kick: 0.72, closed_hat: 0.70 },
    analog_kick: {
      bodyPitch: 48,
      bodyDecay: 0.35,
      pitchEnvAmt: 40,
      pitchEnvDecay: 0.06,
      clickLevel: 0.3,
      clickTone: 0.5,
      driveAmt: 0.15,
      lowShelfGain: 3,
      decay: 0.38,
    },
    membrane: {
      pitch1: 200,
      pitch2: 255,
      coupling12: 0.35,
      wireLevel: 0.55,
      snareWireTension: 0.6,
      tension: 0.55,
      strikeType: 0.5,
      strikeHardness: 0.65,
      decay: 0.1,
    },
    hybrid_snare: {
      bodyPitch: 210,
      wireLevel: 0.45,
      wireTension: 0.55,
      noiseLevel: 0.6,
      noiseNarrowAmt: 0.5,
      noiseWideAmt: 0.3,
      filterEnvAmt: 0.4,
      bumpAmt: 0.45,
      driveAmt: 0.2,
      bodyDecay: 0.095,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.35,
      filterEnvAmt: 0.4,
      transientLevel: 0.6,
      earlyReflections: 0.2,
      decay: 0.15,
    },
    closed_hat: {
      oscTune: 1.1,
      hiFilterFreq: 7500,
      loFilterFreq: 3600,
      noiseLevel: 0.35,
      transientLevel: 0.45,
      spectralDecay: 0.5,
      sizzleDepth: 0.3,
      driveAmt: 0.15,
      decay: 0.035,
    },
    open_hat: {
      oscTune: 1.1,
      hiFilterFreq: 7500,
      loFilterFreq: 3600,
      noiseLevel: 0.4,
      transientLevel: 0.45,
      spectralDecay: 0.55,
      hpfCutoff: 4000,
      sizzleDepth: 0.5,
      driveAmt: 0.15,
      decay: 0.30,
    },
    cymbal: {
      pitch: 400,
      size: 0.4,
      richness: 0.6,
      clusterDensity: 0.55,
      metalCharacter: 0.5,
      topAmt: 0.45,
      decay: 1.0,
    },
    tom: {
      pitch: 120,
      pitchEnvAmt: 12,
      pitchRampDecay: 0.045,
      tension: 0.6,
      overtoneAmt: 0.35,
      clickLevel: 0.3,
      decay: 0.22,
    },
    rim: {
      pitch: 1100,
      dualStrike: 0.1,
      enhance: 0.55,
      feedbackAmt: 0.28,
      snareTimbreAmt: 0.08,
      driveAmt: 0.18,
      decay: 0.05,
    },
    shaker: {
      rattleType: 0.5,
      grainSize: 0.01,
      grainDensity: 110,
      hardness: 0.55,
      tone: 0.6,
      noiseColor: 0.55,
      decay: 0.18,
    },
    fold: {
      pitch: 52,
      foldAmt: 0.5,
      foldSymmetry: 0.45,
      tablePosition: 0.15,
      tableWarp: 0.1,
      pitchEnvAmt: 18,
      filterRes: 0.3,
      decay: 0.32,
    },
    impact: {
      transientType: 'click',
      transientBrightness: 0.6,
      transientLevel: 0.7,
      bodyType: 'resonant',
      bodyPitch: 280,
      bodyDecay: 0.25,
      tailType: 'noise-tail',
      tailDecay: 0.5,
      tailColor: 0.6,
      layerMix: 0.5,
      overallDecay: 0.4,
    },
    fm: {
      pitch: 55,
      modIndex: 5.5,
      modIndexDecay: 0.06,
      brightness: 0.55,
      inharmonicityBias: 0.08,
      driveAmt: 0.2,
      decay: 0.28,
    },
  },

  // ============================================
  // Experimental / IDM
  // ============================================

  'Glitch Lab': {
    _description: 'Fractured rhythms - unstable tones, harsh textures, digital artifacts',
    _category: 'Experimental',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS, closed_hat: 0.65, open_hat: 0.62, cymbal: 0.60, fm: 0.70 },
    analog_kick: {
      bodyPitch: 58,
      bodyDecay: 0.18,
      pitchEnvAmt: 36,
      pitchEnvDecay: 0.04,
      clickLevel: 0.55,
      clickTone: 0.7,
      driveAmt: 0.4,
      lowShelfGain: 0,
      decay: 0.2,
    },
    membrane: {
      pitch1: 230,
      pitch2: 310,
      coupling12: 0.5,
      wireLevel: 0.6,
      snareWireTension: 0.75,
      tension: 0.7,
      strikeType: 0.7,
      strikeHardness: 0.8,
      decay: 0.08,
    },
    hybrid_snare: {
      bodyPitch: 250,
      wireLevel: 0.55,
      wireTension: 0.7,
      noiseLevel: 0.75,
      noiseNarrowAmt: 0.6,
      noiseWideAmt: 0.4,
      filterEnvAmt: 0.5,
      bumpAmt: 0.55,
      driveAmt: 0.4,
      bodyDecay: 0.075,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.4,
      filterEnvAmt: 0.55,
      transientLevel: 0.75,
      earlyReflections: 0.1,
      decay: 0.12,
    },
    closed_hat: {
      oscTune: 1.3,
      hiFilterFreq: 9000,
      loFilterFreq: 4200,
      noiseLevel: 0.45,
      transientLevel: 0.55,
      spectralDecay: 0.65,
      sizzleDepth: 0.4,
      sampleRateReduce: 0.3,
      driveAmt: 0.35,
      decay: 0.025,
    },
    open_hat: {
      oscTune: 1.3,
      hiFilterFreq: 9000,
      loFilterFreq: 4200,
      noiseLevel: 0.5,
      transientLevel: 0.55,
      spectralDecay: 0.7,
      hpfCutoff: 4500,
      sizzleDepth: 0.6,
      sampleRateReduce: 0.3,
      driveAmt: 0.35,
      decay: 0.30,
    },
    cymbal: {
      pitch: 480,
      size: 0.3,
      richness: 0.8,
      clusterDensity: 0.75,
      metalCharacter: 0.7,
      topAmt: 0.6,
      decay: 0.8,
    },
    tom: {
      pitch: 160,
      pitchEnvAmt: 20,
      pitchRampDecay: 0.03,
      tension: 0.8,
      overtoneAmt: 0.55,
      clickLevel: 0.6,
      decay: 0.15,
    },
    rim: {
      pitch: 1500,
      dualStrike: 0.25,
      enhance: 0.75,
      feedbackAmt: 0.45,
      snareTimbreAmt: 0.2,
      driveAmt: 0.4,
      decay: 0.04,
    },
    shaker: {
      rattleType: 0.75,
      grainSize: 0.006,
      grainDensity: 170,
      hardness: 0.8,
      tone: 0.75,
      noiseColor: 0.7,
      decay: 0.12,
    },
    fold: {
      pitch: 70,
      foldAmt: 0.85,
      foldSymmetry: 0.25,
      tablePosition: 0.45,
      tableWarp: 0.35,
      pitchEnvAmt: 20,
      filterRes: 0.5,
      decay: 0.18,
    },
    impact: {
      transientType: 'impulse',
      transientBrightness: 0.85,
      transientLevel: 0.85,
      bodyType: 'noise',
      bodyPitch: 350,
      bodyDecay: 0.15,
      tailType: 'damped',
      tailDecay: 0.35,
      tailColor: 0.75,
      layerMix: 0.4,
      overallDecay: 0.3,
    },
    fm: {
      pitch: 72,
      modIndex: 10,
      modIndexDecay: 0.035,
      brightness: 0.75,
      inharmonicityBias: 0.25,
      driveAmt: 0.45,
      decay: 0.18,
    },
  },

  'IDM Toolkit': {
    _description: 'Complex textures - intricate timbres, evolving sounds, detailed percussion',
    _category: 'Experimental',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS },
    analog_kick: {
      bodyPitch: 52,
      bodyDecay: 0.25,
      pitchEnvAmt: 35,
      pitchEnvDecay: 0.055,
      clickLevel: 0.4,
      clickTone: 0.6,
      driveAmt: 0.22,
      lowShelfGain: 1.5,
      decay: 0.28,
    },
    membrane: {
      pitch1: 210,
      pitch2: 270,
      coupling12: 0.4,
      wireLevel: 0.5,
      snareWireTension: 0.65,
      tension: 0.6,
      strikeType: 0.6,
      strikeHardness: 0.7,
      decay: 0.11,
    },
    hybrid_snare: {
      bodyPitch: 225,
      wireLevel: 0.45,
      wireTension: 0.6,
      noiseLevel: 0.6,
      noiseNarrowAmt: 0.5,
      noiseWideAmt: 0.3,
      filterEnvAmt: 0.4,
      bumpAmt: 0.45,
      driveAmt: 0.25,
      bodyDecay: 0.095,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.35,
      filterEnvAmt: 0.45,
      transientLevel: 0.55,
      earlyReflections: 0.25,
      decay: 0.18,
    },
    closed_hat: {
      oscTune: 1.15,
      hiFilterFreq: 8000,
      loFilterFreq: 3800,
      noiseLevel: 0.4,
      transientLevel: 0.5,
      spectralDecay: 0.55,
      sizzleDepth: 0.35,
      sampleRateReduce: 0.18,
      driveAmt: 0.25,
      decay: 0.042,
    },
    open_hat: {
      oscTune: 1.15,
      hiFilterFreq: 8000,
      loFilterFreq: 3800,
      noiseLevel: 0.45,
      transientLevel: 0.5,
      spectralDecay: 0.6,
      hpfCutoff: 4200,
      sizzleDepth: 0.55,
      sampleRateReduce: 0.18,
      driveAmt: 0.25,
      decay: 0.30,
    },
    cymbal: {
      pitch: 420,
      size: 0.38,
      richness: 0.68,
      clusterDensity: 0.6,
      metalCharacter: 0.55,
      topAmt: 0.5,
      decay: 1.4,
    },
    tom: {
      pitch: 135,
      pitchEnvAmt: 14,
      pitchRampDecay: 0.05,
      tension: 0.65,
      overtoneAmt: 0.45,
      clickLevel: 0.45,
      decay: 0.22,
    },
    rim: {
      pitch: 1250,
      dualStrike: 0.18,
      enhance: 0.65,
      feedbackAmt: 0.35,
      snareTimbreAmt: 0.12,
      driveAmt: 0.25,
      decay: 0.052,
    },
    shaker: {
      rattleType: 0.6,
      grainSize: 0.01,
      grainDensity: 120,
      hardness: 0.65,
      tone: 0.65,
      noiseColor: 0.6,
      decay: 0.18,
    },
    fold: {
      pitch: 62,
      foldAmt: 0.6,
      foldSymmetry: 0.4,
      tablePosition: 0.25,
      tableWarp: 0.18,
      pitchEnvAmt: 16,
      filterRes: 0.38,
      decay: 0.25,
    },
    impact: {
      transientType: 'click',
      transientBrightness: 0.65,
      transientLevel: 0.7,
      bodyType: 'resonant',
      bodyPitch: 300,
      bodyDecay: 0.28,
      tailType: 'reverb-like',
      tailDecay: 0.55,
      tailColor: 0.6,
      layerMix: 0.5,
      overallDecay: 0.45,
    },
    fm: {
      pitch: 65,
      modIndex: 6.5,
      modIndexDecay: 0.055,
      brightness: 0.6,
      inharmonicityBias: 0.12,
      driveAmt: 0.28,
      decay: 0.25,
    },
  },

  'Noise Industrial': {
    _description: 'Raw machine power - grinding textures, metallic chaos, brutal dynamics',
    _category: 'Experimental',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS, analog_kick: 0.68, closed_hat: 0.60, open_hat: 0.58, cymbal: 0.55, fm: 0.65 },
    analog_kick: {
      bodyPitch: 45,
      bodyDecay: 0.15,
      pitchEnvAmt: 48,
      pitchEnvDecay: 0.025,
      clickLevel: 0.7,
      clickTone: 0.85,
      driveAmt: 0.65,
      lowShelfGain: -2,
      decay: 0.18,
    },
    membrane: {
      pitch1: 250,
      pitch2: 340,
      coupling12: 0.55,
      wireLevel: 0.8,
      snareWireTension: 0.85,
      tension: 0.8,
      strikeType: 0.85,
      strikeHardness: 0.95,
      decay: 0.07,
    },
    hybrid_snare: {
      bodyPitch: 280,
      wireLevel: 0.7,
      wireTension: 0.85,
      noiseLevel: 0.9,
      noiseNarrowAmt: 0.7,
      noiseWideAmt: 0.5,
      filterEnvAmt: 0.6,
      bumpAmt: 0.7,
      driveAmt: 0.55,
      bodyDecay: 0.065,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.45,
      filterEnvAmt: 0.6,
      transientLevel: 0.9,
      earlyReflections: 0.05,
      decay: 0.1,
    },
    closed_hat: {
      oscTune: 1.4,
      hiFilterFreq: 10000,
      loFilterFreq: 4500,
      noiseLevel: 0.55,
      transientLevel: 0.6,
      spectralDecay: 0.7,
      sizzleDepth: 0.55,
      sampleRateReduce: 0.4,
      driveAmt: 0.5,
      decay: 0.022,
    },
    open_hat: {
      oscTune: 1.4,
      hiFilterFreq: 10000,
      loFilterFreq: 4500,
      noiseLevel: 0.6,
      transientLevel: 0.6,
      spectralDecay: 0.75,
      hpfCutoff: 5000,
      sizzleDepth: 0.75,
      sampleRateReduce: 0.4,
      driveAmt: 0.5,
      decay: 0.25,
    },
    cymbal: {
      pitch: 520,
      size: 0.25,
      richness: 0.85,
      clusterDensity: 0.8,
      metalCharacter: 0.8,
      topAmt: 0.7,
      decay: 0.6,
    },
    tom: {
      pitch: 180,
      pitchEnvAmt: 22,
      pitchRampDecay: 0.025,
      tension: 0.85,
      overtoneAmt: 0.65,
      clickLevel: 0.7,
      decay: 0.12,
    },
    rim: {
      pitch: 1700,
      dualStrike: 0.3,
      enhance: 0.85,
      feedbackAmt: 0.55,
      snareTimbreAmt: 0.25,
      driveAmt: 0.5,
      decay: 0.035,
    },
    shaker: {
      rattleType: 0.85,
      grainSize: 0.005,
      grainDensity: 190,
      hardness: 0.9,
      tone: 0.85,
      noiseColor: 0.8,
      decay: 0.1,
    },
    fold: {
      pitch: 75,
      foldAmt: 0.95,
      foldSymmetry: 0.15,
      tablePosition: 0.55,
      tableWarp: 0.45,
      pitchEnvAmt: 22,
      filterRes: 0.6,
      decay: 0.15,
    },
    impact: {
      transientType: 'impulse',
      transientBrightness: 0.9,
      transientLevel: 0.9,
      bodyType: 'noise',
      bodyPitch: 400,
      bodyDecay: 0.12,
      tailType: 'damped',
      tailDecay: 0.3,
      tailColor: 0.85,
      layerMix: 0.35,
      overallDecay: 0.25,
    },
    fm: {
      pitch: 80,
      modIndex: 14,
      modIndexDecay: 0.025,
      brightness: 0.85,
      inharmonicityBias: 0.35,
      driveAmt: 0.55,
      decay: 0.15,
    },
  },

  // ============================================
  // Acoustic & Organic
  // ============================================

  'Live Room': {
    _description: 'Acoustic drum kit feel - natural dynamics, room ambience, realistic tones',
    _category: 'Acoustic',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS },
    analog_kick: {
      bodyPitch: 62,
      bodyDecay: 0.22,
      pitchEnvAmt: 18,
      pitchEnvDecay: 0.035,
      clickLevel: 0.25,
      clickTone: 0.4,
      driveAmt: 0.08,
      lowShelfGain: 1,
      decay: 0.26,
    },
    membrane: {
      pitch1: 175,
      pitch2: 215,
      coupling12: 0.35,
      wireLevel: 0.5,
      snareWireTension: 0.55,
      tension: 0.5,
      strikeType: 0.45,
      strikeHardness: 0.55,
      decay: 0.16,
    },
    hybrid_snare: {
      bodyPitch: 185,
      wireLevel: 0.3,
      wireTension: 0.45,
      noiseLevel: 0.45,
      noiseNarrowAmt: 0.35,
      noiseWideAmt: 0.2,
      filterEnvAmt: 0.2,
      bumpAmt: 0.3,
      driveAmt: 0.08,
      bodyDecay: 0.14,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.35,
      filterEnvAmt: 0.3,
      transientLevel: 0.4,
      earlyReflections: 0.45,
      decay: 0.22,
    },
    closed_hat: {
      oscTune: 0.95,
      hiFilterFreq: 6800,
      loFilterFreq: 3300,
      noiseLevel: 0.25,
      transientLevel: 0.35,
      spectralDecay: 0.45,
      sizzleDepth: 0.25,
      driveAmt: 0.08,
      decay: 0.065,
    },
    open_hat: {
      oscTune: 0.95,
      hiFilterFreq: 6800,
      loFilterFreq: 3300,
      noiseLevel: 0.3,
      transientLevel: 0.35,
      spectralDecay: 0.5,
      hpfCutoff: 3800,
      sizzleDepth: 0.45,
      driveAmt: 0.08,
      decay: 0.33,
    },
    cymbal: {
      pitch: 310,
      size: 0.55,
      richness: 0.55,
      clusterDensity: 0.45,
      metalCharacter: 0.4,
      topAmt: 0.35,
      decay: 2.0,
    },
    tom: {
      pitch: 105,
      pitchEnvAmt: 6,
      pitchRampDecay: 0.08,
      tension: 0.5,
      overtoneAmt: 0.3,
      clickLevel: 0.2,
      decay: 0.35,
    },
    rim: {
      pitch: 950,
      dualStrike: 0.05,
      enhance: 0.45,
      feedbackAmt: 0.18,
      snareTimbreAmt: 0.05,
      driveAmt: 0.1,
      decay: 0.072,
    },
    shaker: {
      rattleType: 0.4,
      grainSize: 0.018,
      grainDensity: 70,
      hardness: 0.45,
      tone: 0.5,
      noiseColor: 0.48,
      decay: 0.28,
    },
    fold: {
      pitch: 60,
      foldAmt: 0.25,
      foldSymmetry: 0.5,
      tablePosition: 0.05,
      tableWarp: 0.02,
      pitchEnvAmt: 6,
      filterRes: 0.18,
      decay: 0.3,
    },
    impact: {
      transientType: 'mallet',
      transientBrightness: 0.45,
      transientLevel: 0.5,
      bodyType: 'sine',
      bodyPitch: 200,
      bodyDecay: 0.45,
      tailType: 'reverb-like',
      tailDecay: 0.75,
      tailColor: 0.45,
      layerMix: 0.55,
      overallDecay: 0.6,
    },
    fm: {
      pitch: 58,
      modIndex: 3,
      modIndexDecay: 0.1,
      brightness: 0.4,
      inharmonicityBias: 0,
      driveAmt: 0.1,
      decay: 0.32,
    },
  },

  'Jazz Brushes': {
    _description: 'Soft and swinging - brushed snares, gentle kick, subtle cymbals',
    _category: 'Acoustic',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS, shaker: 0.85 },
    analog_kick: {
      bodyPitch: 58,
      bodyDecay: 0.28,
      pitchEnvAmt: 16,
      pitchEnvDecay: 0.04,
      clickLevel: 0.15,
      clickTone: 0.35,
      driveAmt: 0.05,
      lowShelfGain: 2,
      decay: 0.32,
    },
    membrane: {
      pitch1: 165,
      pitch2: 200,
      coupling12: 0.3,
      wireLevel: 0.35,
      snareWireTension: 0.45,
      tension: 0.45,
      strikeType: 0.25,
      strikeHardness: 0.35,
      decay: 0.2,
    },
    hybrid_snare: {
      bodyPitch: 170,
      wireLevel: 0.22,
      wireTension: 0.35,
      noiseLevel: 0.35,
      noiseNarrowAmt: 0.25,
      noiseWideAmt: 0.15,
      filterEnvAmt: 0.15,
      bumpAmt: 0.22,
      driveAmt: 0.05,
      bodyDecay: 0.18,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.3,
      filterEnvAmt: 0.2,
      transientLevel: 0.3,
      earlyReflections: 0.5,
      decay: 0.28,
    },
    closed_hat: {
      oscTune: 0.9,
      hiFilterFreq: 6200,
      loFilterFreq: 3000,
      noiseLevel: 0.25,
      transientLevel: 0.25,
      spectralDecay: 0.45,
      sizzleDepth: 0.2,
      driveAmt: 0.05,
      decay: 0.08,
    },
    open_hat: {
      oscTune: 0.9,
      hiFilterFreq: 6200,
      loFilterFreq: 3000,
      noiseLevel: 0.3,
      transientLevel: 0.25,
      spectralDecay: 0.5,
      hpfCutoff: 3500,
      sizzleDepth: 0.4,
      driveAmt: 0.05,
      decay: 0.40,
    },
    cymbal: {
      pitch: 290,
      size: 0.6,
      richness: 0.5,
      clusterDensity: 0.38,
      metalCharacter: 0.35,
      topAmt: 0.28,
      decay: 2.4,
    },
    tom: {
      pitch: 95,
      pitchEnvAmt: 4,
      pitchRampDecay: 0.1,
      tension: 0.45,
      overtoneAmt: 0.25,
      clickLevel: 0.12,
      decay: 0.4,
    },
    rim: {
      pitch: 880,
      dualStrike: 0.02,
      enhance: 0.4,
      feedbackAmt: 0.15,
      snareTimbreAmt: 0,
      driveAmt: 0.06,
      decay: 0.08,
    },
    shaker: {
      rattleType: 0.3,
      grainSize: 0.022,
      grainDensity: 58,
      hardness: 0.38,
      tone: 0.45,
      noiseColor: 0.45,
      decay: 0.32,
    },
    fold: {
      pitch: 55,
      foldAmt: 0.18,
      foldSymmetry: 0.52,
      tablePosition: 0.08,
      tableWarp: 0.04,
      pitchEnvAmt: 4,
      filterRes: 0.15,
      decay: 0.35,
    },
    impact: {
      transientType: 'brush',
      transientBrightness: 0.35,
      transientLevel: 0.4,
      bodyType: 'filtered-noise',
      bodyPitch: 180,
      bodyDecay: 0.55,
      tailType: 'shimmer',
      tailDecay: 1.0,
      tailColor: 0.4,
      layerMix: 0.55,
      overallDecay: 0.75,
    },
    fm: {
      pitch: 52,
      modIndex: 2.2,
      modIndexDecay: 0.12,
      brightness: 0.35,
      inharmonicityBias: -0.08,
      driveAmt: 0.06,
      decay: 0.38,
    },
  },

  'World Percussion': {
    _description: 'Global rhythms - hand drums, shakers, wooden percussion, ethnic textures',
    _category: 'Acoustic',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS, shaker: 0.85, tom: 0.82 },
    analog_kick: {
      bodyPitch: 70,
      bodyDecay: 0.2,
      pitchEnvAmt: 12,
      pitchEnvDecay: 0.03,
      clickLevel: 0.3,
      clickTone: 0.45,
      driveAmt: 0.1,
      lowShelfGain: 1,
      decay: 0.24,
    },
    membrane: {
      pitch1: 190,
      pitch2: 235,
      coupling12: 0.4,
      wireLevel: 0.2,
      snareWireTension: 0.4,
      tension: 0.55,
      strikeType: 0.5,
      strikeHardness: 0.6,
      decay: 0.14,
    },
    hybrid_snare: {
      bodyPitch: 195,
      wireLevel: 0.35,
      wireTension: 0.5,
      noiseLevel: 0.38,
      noiseNarrowAmt: 0.35,
      noiseWideAmt: 0.2,
      filterEnvAmt: 0.25,
      bumpAmt: 0.35,
      driveAmt: 0.1,
      bodyDecay: 0.13,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.3,
      filterEnvAmt: 0.3,
      transientLevel: 0.45,
      earlyReflections: 0.35,
      decay: 0.2,
    },
    closed_hat: {
      oscTune: 1.0,
      hiFilterFreq: 7000,
      loFilterFreq: 3400,
      noiseLevel: 0.25,
      transientLevel: 0.35,
      spectralDecay: 0.45,
      sizzleDepth: 0.22,
      driveAmt: 0.1,
      decay: 0.06,
    },
    open_hat: {
      oscTune: 1.0,
      hiFilterFreq: 7000,
      loFilterFreq: 3400,
      noiseLevel: 0.3,
      transientLevel: 0.35,
      spectralDecay: 0.5,
      hpfCutoff: 4000,
      sizzleDepth: 0.42,
      driveAmt: 0.1,
      decay: 0.30,
    },
    cymbal: {
      pitch: 340,
      size: 0.42,
      richness: 0.52,
      clusterDensity: 0.42,
      metalCharacter: 0.42,
      topAmt: 0.38,
      decay: 1.6,
    },
    tom: {
      pitch: 145,
      pitchEnvAmt: 8,
      pitchRampDecay: 0.06,
      tension: 0.58,
      overtoneAmt: 0.4,
      clickLevel: 0.4,
      decay: 0.22,
    },
    rim: {
      pitch: 1050,
      dualStrike: 0.12,
      enhance: 0.55,
      feedbackAmt: 0.25,
      snareTimbreAmt: 0,
      driveAmt: 0.12,
      decay: 0.058,
    },
    shaker: {
      rattleType: 0.5,
      grainSize: 0.016,
      grainDensity: 85,
      hardness: 0.52,
      tone: 0.55,
      noiseColor: 0.52,
      decay: 0.25,
    },
    fold: {
      pitch: 72,
      foldAmt: 0.3,
      foldSymmetry: 0.48,
      tablePosition: 0.12,
      tableWarp: 0.08,
      pitchEnvAmt: 8,
      filterRes: 0.22,
      decay: 0.25,
    },
    impact: {
      transientType: 'mallet',
      transientBrightness: 0.5,
      transientLevel: 0.6,
      bodyType: 'resonant',
      bodyPitch: 240,
      bodyDecay: 0.35,
      tailType: 'noise-tail',
      tailDecay: 0.6,
      tailColor: 0.5,
      layerMix: 0.55,
      overallDecay: 0.5,
    },
    fm: {
      pitch: 68,
      modIndex: 3.5,
      modIndexDecay: 0.08,
      brightness: 0.45,
      inharmonicityBias: 0.02,
      driveAmt: 0.12,
      decay: 0.28,
    },
  },

  // ============================================
  // Hip-Hop & Urban
  // ============================================

  'Boom Bap': {
    _description: '90s NYC flavor - dusty drums, punchy snares, classic hip-hop feel',
    _category: 'Hip-Hop',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS },
    analog_kick: {
      bodyPitch: 50,
      bodyDecay: 0.32,
      pitchEnvAmt: 28,
      pitchEnvDecay: 0.07,
      clickLevel: 0.22,
      clickTone: 0.42,
      driveAmt: 0.15,
      lowShelfGain: 3,
      decay: 0.36,
    },
    membrane: {
      pitch1: 185,
      pitch2: 230,
      coupling12: 0.32,
      wireLevel: 0.52,
      snareWireTension: 0.58,
      tension: 0.52,
      strikeType: 0.48,
      strikeHardness: 0.6,
      decay: 0.13,
    },
    hybrid_snare: {
      bodyPitch: 195,
      wireLevel: 0.38,
      wireTension: 0.5,
      noiseLevel: 0.52,
      noiseNarrowAmt: 0.42,
      noiseWideAmt: 0.25,
      filterEnvAmt: 0.3,
      bumpAmt: 0.38,
      driveAmt: 0.15,
      bodyDecay: 0.12,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.35,
      filterEnvAmt: 0.35,
      transientLevel: 0.52,
      earlyReflections: 0.28,
      decay: 0.18,
    },
    closed_hat: {
      oscTune: 1.0,
      hiFilterFreq: 7100,
      loFilterFreq: 3400,
      noiseLevel: 0.3,
      transientLevel: 0.38,
      spectralDecay: 0.45,
      sizzleDepth: 0.22,
      driveAmt: 0.12,
      decay: 0.048,
    },
    open_hat: {
      oscTune: 1.0,
      hiFilterFreq: 7100,
      loFilterFreq: 3400,
      noiseLevel: 0.35,
      transientLevel: 0.38,
      spectralDecay: 0.5,
      hpfCutoff: 4000,
      sizzleDepth: 0.42,
      driveAmt: 0.12,
      decay: 0.30,
    },
    cymbal: {
      pitch: 330,
      size: 0.48,
      richness: 0.55,
      clusterDensity: 0.48,
      metalCharacter: 0.45,
      topAmt: 0.38,
      decay: 1.5,
    },
    tom: {
      pitch: 108,
      pitchEnvAmt: 7,
      pitchRampDecay: 0.075,
      tension: 0.52,
      overtoneAmt: 0.32,
      clickLevel: 0.25,
      decay: 0.3,
    },
    rim: {
      pitch: 1020,
      dualStrike: 0.08,
      enhance: 0.52,
      feedbackAmt: 0.22,
      snareTimbreAmt: 0.05,
      driveAmt: 0.15,
      decay: 0.062,
    },
    shaker: {
      rattleType: 0.42,
      grainSize: 0.015,
      grainDensity: 78,
      hardness: 0.48,
      tone: 0.52,
      noiseColor: 0.5,
      decay: 0.24,
    },
    fold: {
      pitch: 52,
      foldAmt: 0.38,
      foldSymmetry: 0.48,
      tablePosition: 0.08,
      tableWarp: 0.05,
      pitchEnvAmt: 10,
      filterRes: 0.22,
      decay: 0.35,
    },
    impact: {
      transientType: 'mallet',
      transientBrightness: 0.5,
      transientLevel: 0.55,
      bodyType: 'sine',
      bodyPitch: 210,
      bodyDecay: 0.4,
      tailType: 'reverb-like',
      tailDecay: 0.7,
      tailColor: 0.5,
      layerMix: 0.55,
      overallDecay: 0.55,
    },
    fm: {
      pitch: 54,
      modIndex: 3.8,
      modIndexDecay: 0.09,
      brightness: 0.45,
      inharmonicityBias: 0.02,
      driveAmt: 0.15,
      decay: 0.32,
    },
  },

  'Trap Modern': {
    _description: 'Contemporary trap - rolling hats, 808 slides, aggressive snaps',
    _category: 'Hip-Hop',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS, analog_kick: 0.72, closed_hat: 0.70 },
    analog_kick: {
      bodyPitch: 38,
      bodyDecay: 0.7,
      pitchEnvAmt: 55,
      pitchEnvDecay: 0.15,
      clickLevel: 0.12,
      clickTone: 0.28,
      driveAmt: 0.18,
      lowShelfGain: 5,
      decay: 0.75,
    },
    membrane: {
      pitch1: 195,
      pitch2: 250,
      coupling12: 0.3,
      wireLevel: 0.45,
      snareWireTension: 0.55,
      tension: 0.5,
      strikeType: 0.45,
      strikeHardness: 0.58,
      decay: 0.12,
    },
    hybrid_snare: {
      bodyPitch: 215,
      wireLevel: 0.42,
      wireTension: 0.58,
      noiseLevel: 0.62,
      noiseNarrowAmt: 0.5,
      noiseWideAmt: 0.32,
      filterEnvAmt: 0.4,
      bumpAmt: 0.42,
      driveAmt: 0.22,
      bodyDecay: 0.1,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.38,
      filterEnvAmt: 0.45,
      transientLevel: 0.68,
      earlyReflections: 0.18,
      decay: 0.15,
    },
    closed_hat: {
      oscTune: 1.15,
      hiFilterFreq: 8200,
      loFilterFreq: 3900,
      noiseLevel: 0.38,
      transientLevel: 0.48,
      spectralDecay: 0.55,
      sizzleDepth: 0.32,
      driveAmt: 0.18,
      decay: 0.032,
    },
    open_hat: {
      oscTune: 1.15,
      hiFilterFreq: 8200,
      loFilterFreq: 3900,
      noiseLevel: 0.42,
      transientLevel: 0.48,
      spectralDecay: 0.6,
      hpfCutoff: 4200,
      sizzleDepth: 0.52,
      driveAmt: 0.18,
      decay: 0.30,
    },
    cymbal: {
      pitch: 380,
      size: 0.42,
      richness: 0.6,
      clusterDensity: 0.52,
      metalCharacter: 0.48,
      topAmt: 0.42,
      decay: 1.3,
    },
    tom: {
      pitch: 85,
      pitchEnvAmt: 15,
      pitchRampDecay: 0.08,
      tension: 0.48,
      overtoneAmt: 0.28,
      clickLevel: 0.18,
      decay: 0.42,
    },
    rim: {
      pitch: 1150,
      dualStrike: 0.1,
      enhance: 0.58,
      feedbackAmt: 0.28,
      snareTimbreAmt: 0.08,
      driveAmt: 0.2,
      decay: 0.055,
    },
    shaker: {
      rattleType: 0.55,
      grainSize: 0.011,
      grainDensity: 105,
      hardness: 0.55,
      tone: 0.58,
      noiseColor: 0.55,
      decay: 0.2,
    },
    fold: {
      pitch: 42,
      foldAmt: 0.48,
      foldSymmetry: 0.45,
      tablePosition: 0.15,
      tableWarp: 0.1,
      pitchEnvAmt: 20,
      filterRes: 0.28,
      decay: 0.5,
    },
    impact: {
      transientType: 'brush',
      transientBrightness: 0.45,
      transientLevel: 0.5,
      bodyType: 'filtered-noise',
      bodyPitch: 190,
      bodyDecay: 0.45,
      tailType: 'shimmer',
      tailDecay: 0.8,
      tailColor: 0.45,
      layerMix: 0.55,
      overallDecay: 0.6,
    },
    fm: {
      pitch: 45,
      modIndex: 4.2,
      modIndexDecay: 0.12,
      brightness: 0.42,
      inharmonicityBias: -0.05,
      driveAmt: 0.18,
      decay: 0.45,
    },
  },

  // ============================================
  // Synthesis Showcases
  // ============================================

  'FM Machines': {
    _description: 'Digital precision - metallic FM tones, complex harmonics, DX-style textures',
    _category: 'Synthesis',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS, fm: 0.75, closed_hat: 0.70 },
    analog_kick: {
      bodyPitch: 55,
      bodyDecay: 0.25,
      pitchEnvAmt: 32,
      pitchEnvDecay: 0.05,
      clickLevel: 0.35,
      clickTone: 0.55,
      driveAmt: 0.2,
      lowShelfGain: 1.5,
      decay: 0.28,
    },
    membrane: {
      pitch1: 200,
      pitch2: 260,
      coupling12: 0.35,
      wireLevel: 0.48,
      snareWireTension: 0.6,
      tension: 0.55,
      strikeType: 0.52,
      strikeHardness: 0.65,
      decay: 0.11,
    },
    hybrid_snare: {
      bodyPitch: 215,
      wireLevel: 0.4,
      wireTension: 0.55,
      noiseLevel: 0.55,
      noiseNarrowAmt: 0.45,
      noiseWideAmt: 0.28,
      filterEnvAmt: 0.35,
      bumpAmt: 0.4,
      driveAmt: 0.2,
      bodyDecay: 0.1,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.35,
      filterEnvAmt: 0.4,
      transientLevel: 0.55,
      earlyReflections: 0.22,
      decay: 0.17,
    },
    closed_hat: {
      oscTune: 1.1,
      hiFilterFreq: 8000,
      loFilterFreq: 3800,
      noiseLevel: 0.35,
      transientLevel: 0.45,
      spectralDecay: 0.52,
      sizzleDepth: 0.32,
      driveAmt: 0.2,
      decay: 0.038,
    },
    open_hat: {
      oscTune: 1.1,
      hiFilterFreq: 8000,
      loFilterFreq: 3800,
      noiseLevel: 0.4,
      transientLevel: 0.45,
      spectralDecay: 0.58,
      hpfCutoff: 4200,
      sizzleDepth: 0.52,
      driveAmt: 0.2,
      decay: 0.30,
    },
    cymbal: {
      pitch: 400,
      size: 0.4,
      richness: 0.65,
      clusterDensity: 0.58,
      metalCharacter: 0.55,
      topAmt: 0.48,
      decay: 1.6,
    },
    tom: {
      pitch: 125,
      pitchEnvAmt: 10,
      pitchRampDecay: 0.055,
      tension: 0.58,
      overtoneAmt: 0.38,
      clickLevel: 0.35,
      decay: 0.25,
    },
    rim: {
      pitch: 1200,
      dualStrike: 0.12,
      enhance: 0.6,
      feedbackAmt: 0.32,
      snareTimbreAmt: 0.1,
      driveAmt: 0.22,
      decay: 0.052,
    },
    shaker: {
      rattleType: 0.55,
      grainSize: 0.012,
      grainDensity: 98,
      hardness: 0.58,
      tone: 0.6,
      noiseColor: 0.55,
      decay: 0.2,
    },
    fold: {
      pitch: 60,
      foldAmt: 0.52,
      foldSymmetry: 0.42,
      tablePosition: 0.2,
      tableWarp: 0.12,
      pitchEnvAmt: 15,
      filterRes: 0.32,
      decay: 0.28,
    },
    impact: {
      transientType: 'click',
      transientBrightness: 0.6,
      transientLevel: 0.65,
      bodyType: 'triangle',
      bodyPitch: 280,
      bodyDecay: 0.28,
      tailType: 'damped',
      tailDecay: 0.5,
      tailColor: 0.6,
      layerMix: 0.5,
      overallDecay: 0.42,
    },
    fm: {
      pitch: 62,
      modIndex: 8,
      modIndexDecay: 0.05,
      brightness: 0.65,
      inharmonicityBias: 0.18,
      driveAmt: 0.25,
      decay: 0.25,
    },
  },

  'Wavefolder Lab': {
    _description: 'Analog warmth - rich folds, resonant sweeps, harmonic saturation',
    _category: 'Synthesis',
    _mixerLevels: { ...DEFAULT_MIXER_LEVELS },
    analog_kick: {
      bodyPitch: 48,
      bodyDecay: 0.35,
      pitchEnvAmt: 38,
      pitchEnvDecay: 0.08,
      clickLevel: 0.28,
      clickTone: 0.48,
      driveAmt: 0.28,
      lowShelfGain: 2.5,
      decay: 0.38,
    },
    membrane: {
      pitch1: 185,
      pitch2: 235,
      coupling12: 0.38,
      wireLevel: 0.5,
      snareWireTension: 0.58,
      tension: 0.55,
      strikeType: 0.5,
      strikeHardness: 0.62,
      decay: 0.13,
    },
    hybrid_snare: {
      bodyPitch: 200,
      wireLevel: 0.42,
      wireTension: 0.52,
      noiseLevel: 0.52,
      noiseNarrowAmt: 0.42,
      noiseWideAmt: 0.25,
      filterEnvAmt: 0.32,
      bumpAmt: 0.42,
      driveAmt: 0.22,
      bodyDecay: 0.115,
    },
    clap: {
      noiseLevel: 0.85,
      bodyResAmt: 0.35,
      filterEnvAmt: 0.38,
      transientLevel: 0.52,
      earlyReflections: 0.28,
      decay: 0.19,
    },
    closed_hat: {
      oscTune: 1.05,
      hiFilterFreq: 7400,
      loFilterFreq: 3600,
      noiseLevel: 0.32,
      transientLevel: 0.4,
      spectralDecay: 0.48,
      sizzleDepth: 0.28,
      driveAmt: 0.18,
      decay: 0.042,
    },
    open_hat: {
      oscTune: 1.05,
      hiFilterFreq: 7400,
      loFilterFreq: 3600,
      noiseLevel: 0.38,
      transientLevel: 0.4,
      spectralDecay: 0.52,
      hpfCutoff: 4000,
      sizzleDepth: 0.48,
      driveAmt: 0.18,
      decay: 0.30,
    },
    cymbal: {
      pitch: 360,
      size: 0.48,
      richness: 0.58,
      clusterDensity: 0.52,
      metalCharacter: 0.48,
      topAmt: 0.42,
      decay: 1.8,
    },
    tom: {
      pitch: 115,
      pitchEnvAmt: 9,
      pitchRampDecay: 0.065,
      tension: 0.55,
      overtoneAmt: 0.35,
      clickLevel: 0.28,
      decay: 0.3,
    },
    rim: {
      pitch: 1080,
      dualStrike: 0.1,
      enhance: 0.55,
      feedbackAmt: 0.28,
      snareTimbreAmt: 0.08,
      driveAmt: 0.2,
      decay: 0.058,
    },
    shaker: {
      rattleType: 0.48,
      grainSize: 0.014,
      grainDensity: 88,
      hardness: 0.52,
      tone: 0.55,
      noiseColor: 0.52,
      decay: 0.23,
    },
    fold: {
      pitch: 55,
      foldAmt: 0.72,
      foldSymmetry: 0.38,
      tablePosition: 0.25,
      tableWarp: 0.18,
      pitchEnvAmt: 18,
      filterRes: 0.42,
      decay: 0.35,
    },
    impact: {
      transientType: 'mallet',
      transientBrightness: 0.55,
      transientLevel: 0.6,
      bodyType: 'resonant',
      bodyPitch: 240,
      bodyDecay: 0.35,
      tailType: 'reverb-like',
      tailDecay: 0.65,
      tailColor: 0.55,
      layerMix: 0.52,
      overallDecay: 0.52,
    },
    fm: {
      pitch: 58,
      modIndex: 5,
      modIndexDecay: 0.075,
      brightness: 0.52,
      inharmonicityBias: 0.08,
      driveAmt: 0.22,
      decay: 0.32,
    },
  },
};

// Get list of factory preset names (excluding metadata fields)
export const FACTORY_PRESET_NAMES = Object.keys(FACTORY_PRESETS);

// Check if a preset name is a factory preset
export function isFactoryPreset(name) {
  return FACTORY_PRESETS.hasOwnProperty(name);
}

// Get factory preset by name (returns copy to prevent modification)
export function getFactoryPreset(name) {
  if (!FACTORY_PRESETS[name]) return null;
  return JSON.parse(JSON.stringify(FACTORY_PRESETS[name]));
}

// Get all factory presets grouped by category
export function getPresetsByCategory() {
  const categories = {};

  for (const [name, preset] of Object.entries(FACTORY_PRESETS)) {
    const category = preset._category || 'Other';
    if (!categories[category]) {
      categories[category] = [];
    }
    categories[category].push({
      name,
      description: preset._description || '',
    });
  }

  return categories;
}
